import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useSeo } from "@/hooks/useSeo";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";

const CERTIFICATES = [
  {
    amount: 3000,
    label: "3 000 ₽",
    desc: "Лёгкий ужин вдвоём или романтический завтрак с видом на Петербург",
    highlight: false,
  },
  {
    amount: 5000,
    label: "5 000 ₽",
    desc: "Полноценный вечер с вином и авторской кухней на крыше города",
    highlight: true,
  },
];

const base = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function Sertifikat() {
  const [location] = useLocation();
  const [selected, setSelected] = useState<number | null>(null);
  const [form, setForm] = useState({ buyerName: "", buyerEmail: "", recipientName: "", message: "" });
  const [step, setStep] = useState<"pick" | "form" | "loading" | "success" | "error">("pick");
  const [errorMsg, setErrorMsg] = useState("");
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "Подарочные сертификаты — Паруса на крыше | Подарок в Петербурге",
    description: "Подарите вечер на крыше Петербурга. Сертификаты номиналом 3 000 и 5 000 ₽ — оплата онлайн, отправка на email.",
    keywords: "подарочный сертификат ресторан Петербург, сертификат ужин на крыше СПб, подарок ресторан Паруса, купить сертификат ресторан СПб",
    image: "/opengraph.jpg",
    canonical: "/sertifikat",
  });
  useBreadcrumbs([{ name: "Подарочные сертификаты", path: "/sertifikat" }]);

  useEffect(() => {
    setTimeout(() => setLoaded(true), 80);
    const params = new URLSearchParams(window.location.search);
    if (params.get("status") === "success") setStep("success");
  }, []);

  async function handleOrder() {
    if (!selected || !form.buyerName.trim() || !form.buyerEmail.trim()) return;
    setStep("loading");
    try {
      const res = await fetch(`${base}/api/certificates/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: selected,
          buyerName: form.buyerName.trim(),
          buyerEmail: form.buyerEmail.trim(),
          recipientName: form.recipientName.trim(),
          message: form.message.trim(),
          returnUrl: window.location.origin + base + "/sertifikat?status=success",
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(data.error || "Ошибка при создании заказа");
        setStep("error");
        return;
      }
      if (data.confirmationUrl) {
        window.location.href = data.confirmationUrl;
      } else {
        setErrorMsg(data.error || "Платёжная система ещё не подключена — обратитесь к администратору");
        setStep("error");
      }
    } catch {
      setErrorMsg("Нет соединения с сервером");
      setStep("error");
    }
  }

  const anim = {
    opacity: loaded ? 1 : 0,
    transform: loaded ? "translateY(0)" : "translateY(20px)",
    transition: "opacity 0.6s ease, transform 0.6s ease",
  };

  return (
    <div className="w-full min-h-screen bg-background relative z-10 pt-[100px] md:pt-[120px] pb-32 overflow-x-hidden">
      <div className="container mx-auto px-6 md:px-14 max-w-5xl relative">

        <div className="hidden sm:block absolute top-0 right-0 font-serif text-[110px] md:text-[210px] font-light leading-none text-primary/4 select-none pointer-events-none tracking-tighter -mt-20">Gift</div>

        {/* Header */}
        <div className="mb-14 md:mb-20 relative z-10" style={anim}>
          <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-4">Подарочные сертификаты</div>
          <h1 className="text-4xl md:text-6xl font-serif font-light text-primary mb-6 tracking-wide leading-tight">
            Подарите вечер<br className="hidden md:block" /> на крыше Петербурга
          </h1>
          <div className="w-24 h-[1px] bg-primary/30 mb-6" />
          <p className="text-sm text-muted-foreground font-light max-w-lg leading-relaxed">
            Сертификат действует 12 месяцев. После оплаты приходит на вашу почту — можно распечатать или переслать.
          </p>
        </div>

        {/* SUCCESS */}
        {step === "success" && (
          <div className="border border-primary/30 bg-card/40 p-12 text-center max-w-xl mx-auto" style={anim}>
            <div className="text-4xl mb-6">🎉</div>
            <h2 className="font-serif text-2xl font-light text-primary mb-4">Оплата прошла успешно!</h2>
            <p className="text-sm text-muted-foreground font-light leading-relaxed mb-8">
              Сертификат отправлен на указанный email. Если письмо не пришло — проверьте папку «Спам» или напишите нам.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => { setStep("pick"); setSelected(null); setForm({ buyerName: "", buyerEmail: "", recipientName: "", message: "" }); }}
                className="border border-primary/40 text-accent px-8 py-3 text-[9px] tracking-[0.3em] uppercase hover:bg-primary/10 transition-colors">
                Купить ещё
              </button>
              <a href={`${base}/`} className="bg-primary/20 border border-primary text-accent px-8 py-3 text-[9px] tracking-[0.3em] uppercase hover:bg-primary hover:text-background transition-colors text-center">
                На главную
              </a>
            </div>
          </div>
        )}

        {/* CERTIFICATE CARDS */}
        {(step === "pick" || step === "form") && (
          <div className="relative z-10" style={{ ...anim, transitionDelay: "0.1s" }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
              {CERTIFICATES.map((cert) => (
                <button
                  key={cert.amount}
                  onClick={() => { setSelected(cert.amount); setStep("form"); }}
                  className={`group relative text-left p-0 overflow-hidden transition-all border ${
                    selected === cert.amount
                      ? "border-primary shadow-[0_0_40px_rgba(0,163,128,0.15)]"
                      : "border-primary/20 hover:border-primary/50"
                  }`}
                >
                  {cert.highlight && (
                    <div className="absolute top-4 right-4 text-[8px] tracking-[0.3em] uppercase bg-accent text-background px-2.5 py-1 font-medium z-10">
                      Популярный
                    </div>
                  )}

                  {/* Certificate visual */}
                  <div className="relative h-48 bg-gradient-to-br from-[hsl(166_38%_8%)] to-[hsl(166_38%_4%)] flex flex-col items-center justify-center gap-3 overflow-hidden">
                    <div className="absolute inset-0 opacity-10"
                      style={{ backgroundImage: "radial-gradient(circle at 30% 50%, hsl(166 100% 32%) 0%, transparent 60%)" }} />
                    <div className="absolute inset-0 opacity-5"
                      style={{ backgroundImage: "url('/photos/view_01.jpg')", backgroundSize: "cover", backgroundPosition: "center" }} />
                    <div className="relative z-10 flex flex-col items-center gap-2">
                      <svg width="32" height="32" viewBox="360 545 1280 960" className="text-primary fill-current opacity-60">
                        <path d="M640 1080L960 545L1280 1080H640Z M860 1080L960 900L1060 1080H860Z" />
                      </svg>
                      <span className="font-['Cormorant_SC'] text-[10px] tracking-[0.3em] text-primary/60 uppercase">Паруса на крыше</span>
                    </div>
                    <div className="relative z-10 font-serif text-4xl font-light text-primary tracking-wider">{cert.label}</div>
                    <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                  </div>

                  <div className="p-6">
                    <p className="text-sm text-muted-foreground font-light leading-relaxed mb-4">{cert.desc}</p>
                    <div className={`text-[9px] tracking-[0.3em] uppercase transition-colors ${
                      selected === cert.amount ? "text-accent" : "text-primary/50 group-hover:text-primary"
                    }`}>
                      {selected === cert.amount ? "✓ Выбран" : "Выбрать →"}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* ORDER FORM */}
            {step === "form" && selected && (
              <div className="border border-primary/20 bg-card/30 p-8 md:p-12 max-w-2xl"
                style={{ animation: "fadeInUp 0.4s ease" }}>
                <h2 className="font-serif text-2xl font-light text-primary mb-2">Оформить сертификат</h2>
                <p className="text-[10px] tracking-[0.2em] text-muted-foreground/60 uppercase mb-8">
                  Номинал: {CERTIFICATES.find(c => c.amount === selected)?.label}
                </p>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60 mb-2">Ваше имя *</label>
                      <input
                        value={form.buyerName}
                        onChange={e => setForm({ ...form, buyerName: e.target.value })}
                        placeholder="Иван Иванов"
                        className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground focus:outline-none focus:border-primary transition-colors text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60 mb-2">Email для получения *</label>
                      <input
                        type="email"
                        value={form.buyerEmail}
                        onChange={e => setForm({ ...form, buyerEmail: e.target.value })}
                        placeholder="ivan@example.com"
                        className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground focus:outline-none focus:border-primary transition-colors text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60 mb-2">Кому дарите (необязательно)</label>
                    <input
                      value={form.recipientName}
                      onChange={e => setForm({ ...form, recipientName: e.target.value })}
                      placeholder="Марина"
                      className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground focus:outline-none focus:border-primary transition-colors text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60 mb-2">Пожелание на сертификате (необязательно)</label>
                    <textarea
                      rows={2}
                      value={form.message}
                      onChange={e => setForm({ ...form, message: e.target.value })}
                      placeholder="С днём рождения! Желаю тебе…"
                      className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground focus:outline-none focus:border-primary transition-colors text-sm resize-none"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-2">
                    <button
                      onClick={handleOrder}
                      disabled={!form.buyerName.trim() || !form.buyerEmail.trim()}
                      className="flex-1 bg-primary/20 border border-primary text-accent hover:bg-primary hover:text-background disabled:opacity-40 disabled:cursor-not-allowed py-4 text-[10px] tracking-[0.3em] uppercase font-medium transition-colors"
                    >
                      Оплатить {CERTIFICATES.find(c => c.amount === selected)?.label}
                    </button>
                    <button onClick={() => { setStep("pick"); setSelected(null); }}
                      className="border border-primary/20 text-muted-foreground px-8 py-4 text-[9px] tracking-[0.2em] uppercase hover:text-primary transition-colors">
                      Назад
                    </button>
                  </div>

                  <p className="text-[10px] text-muted-foreground/40 tracking-wider leading-relaxed">
                    Оплата через ЮKassa — карты Visa, МИР, Mastercard, СБП. После оплаты сертификат придёт на указанный email.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* LOADING */}
        {step === "loading" && (
          <div className="flex flex-col items-center justify-center py-32 gap-6">
            <div className="w-10 h-10 border border-primary/30 border-t-accent rounded-full animate-spin" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground/50">Переход к оплате…</span>
          </div>
        )}

        {/* ERROR */}
        {step === "error" && (
          <div className="border border-red-800/40 bg-card/30 p-10 max-w-xl" style={anim}>
            <h2 className="font-serif text-xl font-light text-red-400 mb-3">Что-то пошло не так</h2>
            <p className="text-sm text-muted-foreground font-light mb-6">{errorMsg}</p>
            <button onClick={() => setStep("form")}
              className="border border-primary/30 text-accent px-8 py-3 text-[9px] tracking-[0.3em] uppercase hover:bg-primary/10 transition-colors">
              Попробовать снова
            </button>
          </div>
        )}

        {/* Info block */}
        {step === "pick" && (
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-primary/10 pt-16" style={{ ...anim, transitionDelay: "0.2s" }}>
            {[
              { title: "Срок действия", text: "12 месяцев с даты покупки" },
              { title: "Доставка", text: "PDF на email — мгновенно после оплаты" },
              { title: "Использование", text: "Предъявите сертификат при оплате счёта" },
            ].map(item => (
              <div key={item.title}>
                <div className="font-['Cormorant_SC'] text-xs tracking-[0.25em] text-primary/50 uppercase mb-3">{item.title}</div>
                <p className="text-sm text-muted-foreground font-light">{item.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
